import { humanizeFormula, normalizeFormula } from './formulaTransforms';
import { canConvert, getNameForDataType, parser } from '@thinkalpha/language-services';
import type { ConcreteDataType, ParserResult } from '@thinkalpha/language-services';
import type { IndicatorImportRef } from '@thinkalpha/platform-ws-client/contracts/dictionary.js';
import { inject, injectable } from 'inversify';
import { IndicatorImportRefModel } from 'src/dtos/IndicatorImportRef';
import { fakeConstStringParserResult } from 'src/lib/parser';
import type { AnalyzerFunction } from 'src/lib/parser';
import type { FormulaService } from 'src/services/FormulaService';
import type { ReactBindings } from 'src/types/bindings';

/**
 * Runs formula work on the main thread, one item per macrotask. Posting to a MessageChannel schedules a task
 * without setTimeout's clamping, so a burst of requests (e.g. normalizing every preset of an indicator) yields
 * to input and rendering between items instead of running as one long task.
 */
@injectable()
export class FormulaServiceImpl implements FormulaService {
    #log: ReactBindings['Logger'];
    #workQueue: (() => void)[] = [];
    #channel = new MessageChannel();

    constructor(@inject('Logger') logger: ReactBindings['Logger']) {
        this.#log = logger.getSubLogger({ name: 'FormulaServiceImpl' });
        this.#channel.port1.onmessage = () => this.#processNext();
    }

    #processNext() {
        this.#workQueue.shift()?.();
        if (this.#workQueue.length > 0) {
            this.#channel.port2.postMessage(null);
        }
    }

    #enqueue<T>(work: () => T): Promise<T> {
        return new Promise<T>((resolve, reject) => {
            this.#workQueue.push(() => {
                try {
                    resolve(work());
                } catch (err) {
                    reject(err);
                }
            });
            // Only the first item schedules a task; #processNext keeps the queue draining after that
            if (this.#workQueue.length === 1) {
                this.#channel.port2.postMessage(null);
            }
        });
    }

    async parse(
        formula: string,
        analyzers: AnalyzerFunction[] = [],
        equalsMode?: boolean,
        dataTypeRequired?: ConcreteDataType,
    ): Promise<ParserResult> {
        if (equalsMode && !formula.startsWith('=')) {
            return fakeConstStringParserResult(formula);
        }

        const parsableFormula = (equalsMode ? formula.slice(1) : formula) ?? '';

        let parserResult = await this.#enqueue(() => parser(parsableFormula, {}));

        if (TRACE) {
            this.#log.trace('parse result', { formula: parsableFormula, parserResult });
        }

        if (parserResult.root && analyzers.length > 0) {
            if (TRACE) {
                this.#log.trace('analyzing', { formula: parsableFormula });
            }

            for (const analyzer of analyzers) {
                const result = analyzer(parserResult.root!);
                parserResult = {
                    ...parserResult,
                    root: result,
                };
            }
        }

        if (dataTypeRequired) {
            if (
                !parserResult.root ||
                !parserResult.root.dataType ||
                !canConvert(parserResult.root.dataType, dataTypeRequired)
            ) {
                const newError = {
                    text:
                        `Expected type ${getNameForDataType(dataTypeRequired)}` +
                        (parserResult.root && parserResult.root.dataType
                            ? `, but instead found ${getNameForDataType(parserResult.root.dataType)}`
                            : ''),
                    range: { start: 0, end: parserResult.text.length },
                    source: 'if-then',
                };
                parserResult = { ...parserResult, errors: [...parserResult.errors, newError], valid: false };
            }
        }

        return parserResult;
    }

    async normalize(formula: string, indicatorImports: IndicatorImportRef[]) {
        const aliasToRefMap = new Map(
            indicatorImports.map(
                (x) => [x.alias, x instanceof IndicatorImportRefModel ? x.toContractType() : x] as const,
            ),
        );

        return this.#enqueue(() => normalizeFormula(formula, aliasToRefMap));
    }

    async humanize(formula: string) {
        return this.#enqueue(() => humanizeFormula(formula));
    }
}
